import logger from "../utils/logs/LoggerInstance";

import { UserFragment } from "@/graphql/types";

import mixpanel from "mixpanel-browser";
import IntercomAPI from "../intercom/api";
import {
  getAnalytics,
  Analytics,
  logEvent,
  setUserProperties,
  setUserId,
} from "firebase/analytics";
import Firebase from "../utils/firebase";
import {
  VITE_REACT_APP_MELA_ENV,
  VITE_REACT_APP_MIXPANEL_ID,
} from "../constants/variables";

/* EXAMPLE:
Analytics.log(Analytics.Events.export, {
          selectedItems: selectedItems.length,
          exportMode: 'PDF',
          exportType: 'ExportTipe.Timeline',
        });

Analytics.log(Analytics.Events.viewAccountingSummary);
*/

enum CommonEvents {
  // share task with people inside mela
  accountingFromFile = "accountingFromFile",
  addTag = "addTag", //  param assignTags (number of tag save in the current item)
  archiveTask = "archiveTask", // parameter: tasksCount
  copySelectedItems = "copySelectedItems",
  deleteSelectedItems = "deleteSelectedItems",
  deleteUser = "deleteUser",
  error = "app_error", // error show in alert message to user
  export = "export", // param: selectedItems: selectedItems.length, exportMode: 'PDF' exportType: 'Timeline'
  goToExportedFiles = "goToExportedFiles",
  goToMap = "goToMap",
  goToCalendar = "goToCalendar",
  goToPresenceSummary = "goToPresenceSummary",
  goToClockingsSummary = "goToClockingsSummary",
  goToPeopleCostAnalysis = "goToPeopleCostAnalysis",
  goToCostAnalysis = "goToCostAnalysis",
  goToWorkHours = "goToWorkHours",
  goToTaskInfo = "goToTaskInfo",
  goToTasks = "goToTasks",
  goToKanbanView = "goToKanbanView",
  goToAddChecklistTemplateWithAI = "goToAddChecklistTemplateWithAI",
  cancelAddChecklistTemplateWithAI = "cancelAddChecklistTemplateWithAI",
  itemReadByInfo = "itemReadByInfo",
  newItem = "newItem", // param: item Type
  newTask = "newTask", // onCreate Task
  onCaptionItem = "onCaptionItem",
  onChangeItemAddress = "onChangeItemAddress", // TODO:
  onCopyItem = "onCopyItem",
  onDeleteItem = "onDeleteItem",
  onEditItem = "onEditItem",
  onReplyItem = "onReplyItem",
  onSelectItem = "onSelectItem",
  onShareInsideMela = "onShareInsideMela", // parameters: usersCount groupsCount
  openActivityMediaGallery = "openActivityMediaGallery",
  projects = "projects",
  removeTask = "removeTask", // parameter: tasksCount
  searchResult = "search", // onPress
  sendNotarizationEmail = "sendNotarizationEmail",
  showProfile = "showProfile",
  startUserSession = "startUserSession",
  successfulRegistration = "successfulRegistration",
  test = "TEST", // in task if Mela_env is not PROD ( Tasks.tsx)
  updateBudgetInSummary = "updateBudgetInSummary",
  updateTask = "updateTask", // onUpdate Task
  viewAccountingSummary = "viewAccountingSummary",
  goToActions = "goToActions",
  search_activated_inside_task = "search_activated_inside_task", // parameter filters
  search_by_text_inside_task = "search_by_text_inside_task", // parameter filters
  search_by_filters_inside_task = "search_by_filters_inside_task", // parameter filters
  search_by_text_and_filters_inside_task = "search_by_text_and_filters_inside_task", // parameter filters
  export_task_with_searched_items = "export_task_with_searched_items", // parameter filters
  signUpStep1Registration = "signUpStep1Registration",
  failedRegistration = "failedRegistration",
  goToSignUpRegistration = "goToSignUpRegistration",
  goToClockings = "goToClockings",
  signupLoginWithSocial = "signupLoginWithSocial", // parameter ConnectionType
}

enum WebAppEvents {
  goToDashboard = "goToDashboard",
  goToLicense = "goToLicense",
  createClientApiManager = "createClientApiManager",
  // PAYMENT
  goToPayment = "goToPayment",
  goToLicensePage = "goToLicensePage",
  buyLicenses = "buyLicenses",
  enterBillingData = "enterBillingData",
  saveAndGoToPayment = "saveAndGoToPayment",
  successfulPayment = "successfulPayment",
  payNow = "payNow",
  // ACCOUNTING
  create_accounting_default_section = "create_accounting_default_section",
  workScopeMenuClick = "workScopeMenuClick",
  workScopeEditClick = "workScopeEditClick",
  workScopeSaveClick = "workScopeSaveClick",
  resetDeviceId = "resetDeviceId",
  showPlaylistHelp = "showPlaylistHelp", // parameter path
  analyticsActiveRange = "analyticsActiveRange",
  // CONFIGURATION
  createdConfiguration = "createdConfiguration",
  updatedConfiguration = "updatedConfiguration",
  duplicateTask = "duplicateTask",
  // FINANCE
  goToFinance = "goToFinance",
  financeTabChange = "financeTabChange", // param: tab
  financeInvoiceImportOpen = "financeInvoiceImportOpen",
  financeInvoiceExportOpen = "financeInvoiceExportOpen",
  financeInvoiceView = "financeInvoiceView",
  financeInvoiceReconcileOpen = "financeInvoiceReconcileOpen",
  financeInvoiceReconciliationConfirm = "financeInvoiceReconciliationConfirm",
  financeInvoiceReconciliationCancel = "financeInvoiceReconciliationCancel",
  financeInvoiceForceReconcile = "financeInvoiceForceReconcile",
  financeInvoiceDeleted = "financeInvoiceDeleted",
  financeInvoiceEdited = "financeInvoiceEdited",
  financeInvoiceReconciliationDeleted = "financeInvoiceReconciliationDeleted",
  financeInvoiceAiSuggestRequestClick = "financeInvoiceAiSuggestRequestClick",
  financeInvoiceAiSuggestResponse = "financeInvoiceAiSuggestResponse",
  financeInvoiceAiSuggestionAccept = "financeInvoiceAiSuggestionAccept",
  financeInvoiceAiSuggestionReject = "financeInvoiceAiSuggestionReject",
  financeInvoiceAiSuggestionAcceptAllClick = "financeInvoiceAiSuggestionAcceptAllClick",
  financeInvoiceAiSuggestionRejectAllClick = "financeInvoiceAiSuggestionRejectAllClick",
  financeSupplierCreateOpen = "financeSupplierCreateOpen",
  financeSupplierCreated = "financeSupplierCreated",
  financeSupplierUpdated = "financeSupplierUpdated",
  financeSupplierDeleted = "financeSupplierDeleted",
  financeSupplierViewed = "financeSupplierViewed",
  financeInvoiceImportNoCredits = "financeInvoiceImportNoCredits",
  goToTutorials = "goToTutorials",
  // INTEGRATIONS
  integrationsPrestoDownload = "integrationsPrestoDownload",
  // SAFETY
  goToSafety = "goToSafety",
  safetyTabChange = "safetyTabChange", // param: tab
  // Intake and reading — the health of the Brain pipeline.
  safetyDocUploaded = "safetyDocUploaded", // param: fileCount
  safetyDocRead = "safetyDocRead", // params: matched, categoryId
  safetyDocReadFailed = "safetyDocReadFailed",
  // The resolution funnel: a file Brain could not file by itself, from the
  // dashboard row to a filed document.
  safetyDocApplyOpen = "safetyDocApplyOpen",
  safetyDocSubjectCreateOpen = "safetyDocSubjectCreateOpen",
  safetyDocSubjectCreated = "safetyDocSubjectCreated", // param: kind
  safetyDocApplyConfirm = "safetyDocApplyConfirm", // params: kind, viaCreate
  // After filing, as with the invoice pair.
  safetyDocEdited = "safetyDocEdited",
  safetyDocDeleted = "safetyDocDeleted", // param: count
  safetyDocDetached = "safetyDocDetached",
  safetyDocArchived = "safetyDocArchived",
  safetyDocUnarchived = "safetyDocUnarchived",
  safetyDocNewVersion = "safetyDocNewVersion",
  safetyDocShared = "safetyDocShared",
  safetyDocSubjectIdentityApplied = "safetyDocSubjectIdentityApplied",
  // UNIQUE CHAT
  uniqueChatOpened = "uniqueChatOpened",
  uniqueChatClosed = "uniqueChatClosed",
  uniqueChatListOpened = "uniqueChatListOpened",
  uniqueChatAttachmentMenuOpened = "uniqueChatAttachmentMenuOpened",
  uniqueChatLocalAttachmentPickerOpened = "uniqueChatLocalAttachmentPickerOpened",
  uniqueChatMelaCloudModalOpened = "uniqueChatMelaCloudModalOpened",
  uniqueChatAttachmentPreviewOpened = "uniqueChatAttachmentPreviewOpened",
  uniqueChatDictationStarted = "uniqueChatDictationStarted",
  uniqueChatGreetingSuggestionClick = "uniqueChatGreetingSuggestionClick",
  UNIQUE_CHAT_STOP_REQUESTED = "UNIQUE_CHAT_STOP_REQUESTED",
}

export const Events = {
  ...CommonEvents,
  ...WebAppEvents,
};

let currentUser: UserFragment = null;

const MIXPANEL_EU_API_HOST = "https://api-eu.mixpanel.com";

const sendEventToIntercom = (eventName, params) => {
  const event = {
    type: "event",
    event_name: eventName,
    created_at: Math.floor(Date.now() / 1000),
    user_id: currentUser?.id || "anonymous",
    email: currentUser?.email || "anonymous@example.com",
    metadata: params || {},
  };
  IntercomAPI("trackEvent", eventName, event);
};

const CustomAnalytics = () => {
  let analytics: Analytics;

  const init = () => {
    const app = Firebase.init();
    analytics = getAnalytics(app);
    if (VITE_REACT_APP_MIXPANEL_ID) {
      mixpanel.init(VITE_REACT_APP_MIXPANEL_ID, {
        api_host: MIXPANEL_EU_API_HOST,
        debug: VITE_REACT_APP_MELA_ENV !== "prod",
        track_pageview: true,
        persistence: "localStorage",
      });
    }
  };

  const log = async (
    eventKey: CommonEvents | WebAppEvents,
    params?: { [key: string]: any },
    mixpanelExtraParams?: { [key: string]: any },
  ) => {
    logger.debug(
      `Send Analytics. name: ${eventKey} currentUser: ${currentUser?.email}`,
      ["analytics"],
      {
        params,
        mixpanelExtraParams,
      },
    );
    if (
      currentUser?.email?.endsWith("@mela.work") &&
      VITE_REACT_APP_MELA_ENV !== "dev"
    ) {
      // send events in dev for testing - better to remove when tests end
      logger.debug(
        `Send Analytics. no event for currentUser: ${currentUser.email}`,
        ["analytics"],
        params,
      );
      return;
    }

    logEvent(analytics, eventKey.toString(), params);
    sendEventToIntercom(eventKey.toString(), params);
    if (VITE_REACT_APP_MIXPANEL_ID) {
      mixpanel.track(eventKey.toString(), {
        ...(params || {}),
        ...(mixpanelExtraParams || {}),
        company: currentUser?.company?.id || "anonymous",
      });
    }
  };
  const setUser = async (user: UserFragment) => {
    if (user) {
      logger.debug(`setUser ${user?.id}`, ["analytics", "user"]);
      currentUser = user;
      const userCreatedAt = user.createdAt
        ? new Date(user.createdAt)
        : new Date();
      const createdAt = [
        userCreatedAt.getFullYear(),
        userCreatedAt.getMonth() + 1,
      ].join("-");
      if (user.licenseType && user.appLanguage) {
        setUserProperties(analytics, {
          licenseType: user.licenseType,
          appLanguage: user.appLanguage,
          createdAt,
        });
      }
      if (user.id) {
        setUserId(analytics, user.id);
      }
      if (VITE_REACT_APP_MIXPANEL_ID) {
        mixpanel.identify(currentUser?.email);
      }
      // mixpanel.reset(); TODO this is to set at user logout
    }
  };

  return {
    init,
    log,
    setUser,
    Events,
  };
};

export default CustomAnalytics();
